/**
 * apps/web :: lib/paddle-checkout-api
 *
 * Typed wrappers around the `/api/billing/paddle/*` surface (ROADS-451
 * PR-2). Field names mirror the backend wire shape verbatim
 * (`@SerialName` snake_case) — same convention as the Stripe-lane
 * `checkout-api.ts`.
 *
 * Endpoints:
 *   - POST /api/billing/paddle/checkout → 200 {checkout_url, transaction_id}
 *                                          400 validation_failed (tier / shape)
 *                                          401 unauthenticated
 *                                          503 paddle_unavailable
 *   - POST /api/billing/paddle/upgrade  → 200 {subscription_id, new_tier}
 *                                          400 validation_failed (tier)
 *                                          401 unauthenticated
 *                                          404 no_active_subscription
 *                                          503 paddle_unavailable
 *
 * Proration: Paddle handles tourist→Class C credit math server-side via
 * `PATCH /subscriptions/{id}`. The FE does NOT carry an
 * `upgrade_from_entitlement_id` here — the backend resolves the active
 * tourist row from the authenticated user, and the webhook applies the
 * credit. This is intentional symmetry-break from `checkout-api.ts`.
 *
 * Auth: requires the `rb_session` cookie; `apiFetch` always sends
 * credentials. 401s bubble up as `ApiError` and `ProtectedRoute`
 * handles the bounce to /login at the parent route.
 */
import { apiFetch } from "@/lib/api";
import type { EntitlementTier } from "@/lib/entitlement-api";

/** Request body shape for the Paddle checkout POST. */
export interface PaddleCheckoutRequest {
  tier: EntitlementTier;
  state_code?: string | null;
  country_code?: string | null;
}

/** Response body for the Paddle checkout success path. */
export interface PaddleCheckoutResponse {
  checkout_url: string;
  transaction_id: string;
}

/** Request body shape for the Paddle in-place upgrade POST. */
export interface PaddleUpgradeRequest {
  new_tier: EntitlementTier;
}

/**
 * Response body for the Paddle in-place upgrade success path. `new_tier`
 * is returned as a plain string to absorb any backend-side tier renames
 * without a forced FE redeploy; callers that need the closed union can
 * narrow at the call site.
 */
export interface PaddleUpgradeResponse {
  subscription_id: string;
  new_tier: string;
}

/**
 * Create a Paddle checkout transaction for the authenticated user. The
 * caller redirects the browser to `checkout_url` via
 * `window.location.href` to hand off to Paddle's hosted checkout page
 * (paddle.com).
 *
 * Throws `ApiError` on any non-2xx; the page-level handler maps:
 *   - status 400 → "There's a problem with your selection — pick a
 *                  state below and try again" (validation feedback).
 *   - status 401 → ProtectedRoute bounces to /login on next render.
 *   - status 503 → "Checkout is temporarily unavailable. Please try
 *                  again in a minute."
 */
export async function createPaddleCheckoutSession(
  body: PaddleCheckoutRequest,
): Promise<PaddleCheckoutResponse> {
  const wireBody: Record<string, unknown> = { tier: body.tier };
  if (body.state_code) wireBody.state_code = body.state_code;
  if (body.country_code) wireBody.country_code = body.country_code;
  return apiFetch<PaddleCheckoutResponse>("/api/billing/paddle/checkout", {
    method: "POST",
    body: JSON.stringify(wireBody),
  });
}

/**
 * Upgrade the authenticated user's active Paddle subscription to a new
 * tier in-place. Paddle handles proration server-side on
 * `PATCH /subscriptions/{id}` — the FE doesn't compute or display a
 * client-side credit estimate for this lane.
 *
 * Throws `ApiError`:
 *   - status 400 → invalid `new_tier` (e.g. downgrade / unsupported).
 *   - status 404 → no active subscription to upgrade — caller should
 *                  fall back to the checkout-create path.
 *   - status 503 → paddle_unavailable; retry later.
 */
export async function upgradePaddleSubscription(
  body: PaddleUpgradeRequest,
): Promise<PaddleUpgradeResponse> {
  return apiFetch<PaddleUpgradeResponse>("/api/billing/paddle/upgrade", {
    method: "POST",
    body: JSON.stringify({ new_tier: body.new_tier }),
  });
}
